import { TFunction } from 'i18next';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { Task, TaskKindEnum } from 'graphql-common';
import TextLinkCell from '@lib/components/Table/commonComponents/TextLinkCell/TextLinkCell';
import ChipCell from '@lib/components/Table/commonComponents/ChipCell/ChipCell';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import doNotRepeatIconSrc from '@lib/assets/icons/custom_do_not_repeat.svg';
import TaskStatusChip from '@lib/components/TaskStatusChip/TaskStatusChip';
import { getPriorityChipLabel, getPriorityChipType } from '@lib/utils/priority';
import { CellProps, Column } from '@lib/components/Table/types';
import { APP_URLS } from 'constants/urls';
import styles from '../TasksTab.module.scss';

type ColumnsArgs = {
  t: TFunction<'translation', undefined>;
};

export default function getTasksColumns({ t }: ColumnsArgs): Column<Task>[] {
  return [
    {
      id: 'recurringRule',
      Header: ' ',
      accessor: 'recurringRuleId',
      Cell: (props: CellProps<Task>) => {
        const isOnDemand = _get(props, 'item.kind') === TaskKindEnum.OnDemand;
        const isRepeatable = !_isEmpty(_get(props, 'value'));
        if (isOnDemand) {
          return (
            <Chip
              className={styles.repeatStatusChip}
              type={ChipTypes.chipViolet}
              leftIcon="arrow_range"
              leftIconSymbolsOutlined
              tooltip={t('on-demand')}
              asBlockElement
            />
          );
        }
        return (
          <Chip
            className={styles.repeatStatusChip}
            type={isRepeatable ? ChipTypes.chipBlue : ChipTypes.chipGray}
            leftIcon={isRepeatable ? 'repeat' : undefined}
            leftImage={isRepeatable ? undefined : doNotRepeatIconSrc}
            tooltip={isRepeatable ? t('recurring') : t('non-recurring')}
            asBlockElement
          />
        );
      },
      width: 44,
    },
    {
      id: 'name',
      Header: t('name'),
      accessor: 'name',
      Cell: (props: CellProps<Task>) => (
        <TextLinkCell
          {...props}
          to={APP_URLS.dashboard.tasks.view.getDynamicPath({
            pathParts: { id: props?.item.id },
          })}
        />
      ),
      sortable: true,
      minWidth: 250,
      maxWidth: 350,
    },
    {
      id: 'formCategoryName',
      Header: t('form-category'),
      Cell: (props: CellProps<Task>) => {
        const label = _get(props, 'item.formTemplate.category.name');
        const type = colorToChipType(
          _get(props, 'item.formTemplate.category.color'),
        );
        if (!label || !type) return '-';
        return <ChipCell label={label} type={type} leftIcon="circle" />;
      },
      sortable: true,
      width: 200,
    },
    {
      id: 'siteName',
      Header: t('site'),
      accessor: 'site',
      Cell: (props: CellProps<Task>) => {
        const label = _get(props, 'item.site.name', '');
        if (!label) return '-';
        return <ChipCell label={label} type={ChipTypes.chipGray} />;
      },
      sortable: true,
      maxWidth: 250,
      minWidth: 130,
    },
    {
      id: 'siteAreaName',
      Header: t('area'),
      accessor: 'siteArea',
      Cell: (props: CellProps<Task>) => {
        const label = _get(props, 'item.siteArea.name', '');
        if (!label) return '-';
        return <ChipCell label={label} type={ChipTypes.chipGray} />;
      },
      sortable: true,
      maxWidth: 250,
      minWidth: 130,
    },
    {
      id: 'priority',
      Header: t('priority'),
      accessor: 'priority',
      Cell: (props: CellProps<Task>) => {
        const priority = _get(props, 'item.priority');
        const label = getPriorityChipLabel(priority);
        if (!label) return '-';
        return (
          <ChipCell
            label={label}
            type={getPriorityChipType(priority)}
            leftIcon="circle"
          />
        );
      },
      sortable: true,
      width: 120,
    },
    {
      id: 'status',
      Header: t('status'),
      accessor: 'status',
      Cell: ({ item }: CellProps<Task>) => <TaskStatusChip task={item} />,
      sortable: true,
      minWidth: 150,
    },
  ];
}
